import deliveryMethods from '../config/delivery-methods.json'
import { plainText } from './seoText'

/**
 * Canonical variant (Kanoninen variantti – ohje fronttitiimille, 29.9.2026).
 *
 * Size variants of the same colour point their canonical to one page. The canonical page is chosen
 * by the backend and comes in the search document (canonicalPath, isCanonical), so the front never
 * decides it. URLs, routing and robots (INDEX,FOLLOW) do not change.
 */

export interface SeoProduct {
  sku: string
  slug: string
  type?: string
  parentSku?: string
  canonicalPath?: string
  name: { default: string, variation?: string }
  technicalSpecifications?: {
    color?: { value?: string }
    size?: { value?: string }
  }
  marketing?: { productManufacturer?: { value?: string | string[] } }
  description?: { short?: string | null }
  defaultImage?: { url?: string }
  finalPrice: { inclTax: number }
  stock?: { stockStatus?: string }
  /** Delivery method ids (config/delivery-methods.json) */
  shippingCosts?: number[]
}

/** The family data the ProductGroup needs, small enough to send with the page */
export interface FamilyVariant {
  sku: string
  slug: string
  name: string
  color?: string
  size?: string
  image?: string
  price: number
  stockStatus?: string
}

export const isVariant = (product: SeoProduct) => product.type === 'configurable' && !!product.parentSku

/** canonicalPath has no host and no leading slash. Without it the page is its own canonical (as before). */
export function productCanonicalUrl(product: SeoProduct, baseUrl: string): string {
  const path = product.canonicalPath || `product/${product.slug}`
  return `${baseUrl}/${path}`
}

/** "{name.default}, {colour}" on variant pages (no size: the sizes of a colour are one canonical page) */
export function productPageTitle(product: SeoProduct): string {
  const color = product.technicalSpecifications?.color?.value
  if (!isVariant(product) || !color) return product.name.default || ''
  return `${product.name.default}, ${color.toLocaleLowerCase('fi')}`
}

const AVAILABILITY: Record<string, string> = {
  inStock: 'https://schema.org/InStock',
  backorder: 'https://schema.org/BackOrder',
  outOfStock: 'https://schema.org/OutOfStock'
}

export const availability = (stockStatus?: string) => AVAILABILITY[stockStatus || ''] || AVAILABILITY.outOfStock

/**
 * Return policy for the product offers (cms/asiakaspalvelu/palautus-reklamaatio-ja-takuu, 22.10.2026): 14 days for
 * consumers, by mail (Posti/Matkahuolto) or in a store. Not for spare parts: their terms are not stated.
 */
const MERCHANT_RETURN_POLICY = {
  '@type': 'MerchantReturnPolicy',
  'applicableCountry': 'FI',
  'returnPolicyCategory': 'https://schema.org/MerchantReturnFiniteReturnWindow',
  'merchantReturnDays': 14,
  'returnMethod': ['https://schema.org/ReturnByMail', 'https://schema.org/ReturnInStore']
}

/** Posti and Matkahuolto parcels and store pickup: returned free to a Posti/Matkahuolto outlet or a store */
const FREE_RETURN_DELIVERY_METHODS = new Set([
  101, 103, 104, 105, 106, 107, 108, 109, 110, 111, 113, 114,
  201, 202, 203, 204, 205, 206,
  401, 402, 403
])

/** Email (500) and free delivery (502) say nothing about how the product travels */
const NEUTRAL_DELIVERY_METHODS = new Set([500, 502])

/**
 * returnFees (Search Console: Missing field "returnFees", Toni 23.10.2026): free, unless the product can only be
 * delivered by freight or as a vehicle ("rahdilla tilattujen tuotteiden palautus on aina maksullista"). Then the
 * customer pays, without an amount. Without delivery methods the default (free).
 */
export function merchantReturnPolicy(shippingCosts?: number[]) {
  const methods = (shippingCosts || []).filter(id => !NEUTRAL_DELIVERY_METHODS.has(id))
  const freightOnly = methods.length > 0 && !methods.some(id => FREE_RETURN_DELIVERY_METHODS.has(id))
  return {
    ...MERCHANT_RETURN_POLICY,
    returnFees: freightOnly ? 'https://schema.org/ReturnFeesCustomerResponsibility' : 'https://schema.org/FreeReturn'
  }
}

/**
 * shippingDetails: the same rows as the Google Shopping feed (backend, google_shopping_v2.xml), so the page and
 * Merchant Center agree: one per delivery method of the product with a price, the cheapest first. The prices are
 * Futur's, as in config/delivery-methods.json (16.10.2026). Store pickups (0 €) and email are left out, as in the feed;
 * free delivery (502) stays. Delivery abroad is left out too: the destination here is Finland.
 * Delivery times are the feed's constants: handling 1–2 and transit 2–4 business days.
 */
const SHIPPING_DESTINATION = { '@type': 'DefinedRegion', 'addressCountry': 'FI' }
const DELIVERY_TIME = {
  '@type': 'ShippingDeliveryTime',
  'handlingTime': { '@type': 'QuantitativeValue', 'minValue': 1, 'maxValue': 2, 'unitCode': 'DAY' },
  'transitTime': { '@type': 'QuantitativeValue', 'minValue': 2, 'maxValue': 4, 'unitCode': 'DAY' }
}
const FREE_DELIVERY = 502

export function shippingDetails(shippingCosts?: number[]) {
  return (deliveryMethods as Array<{ id: number, price: number, group: string }>)
    .filter(method => shippingCosts?.includes(method.id))
    .filter(method => method.group !== 'DeliveryAbroad' && (method.price > 0 || method.id === FREE_DELIVERY))
    .sort((a, b) => a.price - b.price)
    .map(method => ({
      '@type': 'OfferShippingDetails',
      'shippingRate': { '@type': 'MonetaryAmount', 'value': method.price, 'currency': 'EUR' },
      'shippingDestination': SHIPPING_DESTINATION,
      'deliveryTime': DELIVERY_TIME
    }))
}

/** Offer fields that follow from the delivery methods: shipping and returns */
export function offerDelivery(shippingCosts?: number[]) {
  const shipping = shippingDetails(shippingCosts)
  return {
    ...(shipping.length > 0 && { shippingDetails: shipping }),
    hasMerchantReturnPolicy: merchantReturnPolicy(shippingCosts)
  }
}

/** The manufacturer is an array in the search document; Brand.name must be a string */
export const brandName = (product: SeoProduct) => {
  const value = product.marketing?.productManufacturer?.value
  return (Array.isArray(value) ? value[0] : value) || ''
}

export function toFamilyVariant(product: SeoProduct): FamilyVariant {
  return {
    sku: product.sku,
    slug: product.slug,
    name: product.name.variation || product.name.default,
    color: product.technicalSpecifications?.color?.value,
    size: product.technicalSpecifications?.size?.value,
    image: product.defaultImage?.url,
    price: product.finalPrice.inclTax,
    stockStatus: product.stock?.stockStatus
  }
}

/**
 * Same ProductGroup on every variant page of a family: every variant with its own URL (not the canonical),
 * price and availability, so Google knows each size's stock even when the canonical size is sold out.
 * Shipping and the return policy are the page product's: the family query does not return delivery methods, and
 * the variants of a family are delivered the same way. So is the description (Rich Results: "description puuttuu"):
 * the family query does not return it, and the short description is the family's.
 */
export function productGroupStructuredData(product: SeoProduct, variants: FamilyVariant[], baseUrl: string) {
  const variesBy = [
    variants.some(v => v.color) && 'https://schema.org/color',
    variants.some(v => v.size) && 'https://schema.org/size'
  ].filter(Boolean)
  const delivery = offerDelivery(product.shippingCosts)
  const description = plainText(product.description?.short)

  return {
    '@context': 'https://schema.org',
    '@type': 'ProductGroup',
    'name': product.name.default,
    ...(description && { description }),
    'productGroupID': product.parentSku,
    'variesBy': variesBy,
    'brand': { '@type': 'Brand', 'name': brandName(product) },
    'hasVariant': variants.map(variant => ({
      '@type': 'Product',
      'sku': variant.sku,
      'name': variant.name,
      ...(description && { description }),
      ...(variant.color && { color: variant.color }),
      ...(variant.size && { size: variant.size }),
      'url': `${baseUrl}/product/${variant.slug}`,
      ...(variant.image && { image: variant.image }),
      'offers': {
        '@type': 'Offer',
        'price': variant.price.toFixed(2),
        'priceCurrency': 'EUR',
        'availability': availability(variant.stockStatus),
        ...delivery
      }
    }))
  }
}
